import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  $createParagraphNode,
  $insertNodes,
  $createTextNode,
  PASTE_COMMAND,
  COMMAND_PRIORITY_HIGH,
} from 'lexical';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
  ListNode,
  ListItemNode,
} from '@lexical/list';
import { $isHeadingNode, HeadingNode, QuoteNode } from '@lexical/rich-text';
import { $getNearestNodeOfType } from '@lexical/utils';

import { ValidationError } from '../../components';
import { injectIntl, intlShape, useIntl } from '../../util/reactIntl';
import {
  Bold,
  Italic,
  UnorderedList,
  OrderedList,
  MediaEmbed,
} from './formattingIcons';
import {
  YouTubeEmbedNode,
  $createYouTubeEmbedNode,
  extractYouTubeVideoId,
  isYouTubeUrl,
  SoundCloudEmbedNode,
  $createSoundCloudEmbedNode,
  isSoundCloudUrl,
  VimeoEmbedNode,
  $createVimeoEmbedNode,
  extractVimeoVideoId,
  isVimeoUrl,
} from './nodes';
import { showToaster } from '../../util/genericHelpers';
import { containsBlockedLink } from '../../util/allowedLinks';
import css from './FieldRichTextInput.module.css';

// Toolbar Component
function ToolbarPlugin({ intl }) {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [blockType, setBlockType] = useState('paragraph');
  const [showEmbedDropdown, setShowEmbedDropdown] = useState(false);
  const [embedUrl, setEmbedUrl] = useState('');
  const [embedError, setEmbedError] = useState('');
  const embedDropdownRef = useRef(null);

  // Update toolbar state
  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));

      const anchorNode = selection.anchor.getNode();
      let element = anchorNode.getKey() === 'root'
        ? anchorNode
        : anchorNode.getTopLevelElementOrThrow();

      // Check if we're inside a list by looking at ancestors
      const parentList = $getNearestNodeOfType(anchorNode, ListNode);

      if (parentList) {
        setBlockType(parentList.getListType());
      } else if ($isListNode(element)) {
        setBlockType(element.getListType());
      } else {
        setBlockType($isHeadingNode(element) ? element.getTag() : element.getType());
      }
    }
  }, []);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => updateToolbar());
    });
  }, [editor, updateToolbar]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (embedDropdownRef.current && !embedDropdownRef.current.contains(event.target)) {
        setShowEmbedDropdown(false);
        setEmbedUrl('');
        setEmbedError('');
      }
    };

    if (showEmbedDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmbedDropdown]);

  // Formatting functions
  const formatText = (format) => {
    editor.focus();
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const formatList = (listType) => {
    // Focus the editor first to ensure we have a valid selection
    editor.focus();

    // Use a small timeout to ensure focus is complete before dispatching
    setTimeout(() => {
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return;
        }

        // Dispatch the appropriate list command
        if (blockType === listType) {
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        } else if (listType === 'number') {
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
        } else {
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
        }
      });
    }, 0);
  };

  // Insert embed
  const insertEmbed = useCallback(() => {
    const url = embedUrl.trim();
    if (!url) {
      setEmbedError(intl.formatMessage({ id: 'FieldRichTextInput.embedUrlRequired' }));
      return;
    }

    let videoId = null;
    let isYouTube = false;
    let isVimeo = false;
    let isSoundCloud = false;

    if (isYouTubeUrl(url)) {
      videoId = extractYouTubeVideoId(url);
      if (videoId) {
        isYouTube = true;
      }
    } else if (isVimeoUrl(url)) {
      videoId = extractVimeoVideoId(url);
      if (videoId) {
        isVimeo = true;
      }
    } else if (isSoundCloudUrl(url)) {
      isSoundCloud = true;
    }

    if (isYouTube || isVimeo || isSoundCloud) {
      editor.update(() => {
        let embedNode;
        if (isYouTube) {
          embedNode = $createYouTubeEmbedNode(videoId);
        } else if (isVimeo) {
          embedNode = $createVimeoEmbedNode(videoId);
        } else {
          embedNode = $createSoundCloudEmbedNode(url);
        }
        $insertNodes([embedNode, $createParagraphNode()]);
      });
      setShowEmbedDropdown(false);
      setEmbedUrl('');
      setEmbedError('');
    } else {
      setEmbedError(intl.formatMessage({ id: 'FieldRichTextInput.embedInvalidUrl' }));
    }
  }, [editor, embedUrl, intl]);

  const handleEmbedKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      insertEmbed();
    } else if (e.key === 'Escape') {
      setShowEmbedDropdown(false);
      setEmbedUrl('');
      setEmbedError('');
    }
  };

  // Toolbar button labels
  const boldLabel = intl.formatMessage({ id: 'FieldRichTextInput.bold' });
  const italicLabel = intl.formatMessage({ id: 'FieldRichTextInput.italic' });
  const unorderedListLabel = intl.formatMessage({ id: 'FieldRichTextInput.unorderedList' });
  const orderedListLabel = intl.formatMessage({ id: 'FieldRichTextInput.orderedList' });
  const embedLabel = intl.formatMessage({ id: 'FieldRichTextInput.embed' });

  return (
    <div className={css.toolbar}>
      <button
        className={classNames(css.toolbarButton, { [css.active]: isBold })}
        onClick={() => formatText('bold')}
        type="button"
        aria-label={boldLabel}
        title={boldLabel}
      >
        <Bold />
      </button>
      <button
        className={classNames(css.toolbarButton, { [css.active]: isItalic })}
        onClick={() => formatText('italic')}
        type="button"
        aria-label={italicLabel}
        title={italicLabel}
      >
        <Italic />
      </button>
      <button
        className={classNames(css.toolbarButton, { [css.active]: blockType === 'bullet' })}
        onClick={() => formatList('bullet')}
        type="button"
        aria-label={unorderedListLabel}
        title={unorderedListLabel}
      >
        <UnorderedList />
      </button>
      <button
        className={classNames(css.toolbarButton, { [css.active]: blockType === 'number' })}
        onClick={() => formatList('number')}
        type="button"
        aria-label={orderedListLabel}
        title={orderedListLabel}
      >
        <OrderedList />
      </button>
      <div className={css.embedButtonWrapper} ref={embedDropdownRef}>
        <button
          className={classNames(css.toolbarButton, { [css.active]: showEmbedDropdown })}
          onClick={() => setShowEmbedDropdown(!showEmbedDropdown)}
          type="button"
          aria-label={embedLabel}
          title={embedLabel}
        >
          <MediaEmbed />
        </button>
        {showEmbedDropdown && (
          <div className={css.embedDropdown}>
            <div className={css.embedDropdownHeader}>
              {intl.formatMessage({ id: 'FieldRichTextInput.embedTitle' })}
            </div>
            <input
              type="text"
              className={css.embedInput}
              placeholder={intl.formatMessage({ id: 'FieldRichTextInput.embedPlaceholder' })}
              value={embedUrl}
              onChange={(e) => {
                setEmbedUrl(e.target.value);
                setEmbedError('');
              }}
              onKeyDown={handleEmbedKeyDown}
              autoFocus
            />
            {embedError && <div className={css.embedError}>{embedError}</div>}
            <button
              type="button"
              className={css.embedInsertButton}
              onClick={insertEmbed}
            >
              {intl.formatMessage({ id: 'FieldRichTextInput.embedInsert' })}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Content Initialization Plugin
function InitializeContentPlugin({ initialValue }) {
  const [editor] = useLexicalComposerContext();
  const isInitialized = useRef(false);
  const lastInitialValue = useRef(initialValue);

  useEffect(() => {
    // If initialValue changes to empty string, clear the editor
    if (initialValue === '' && lastInitialValue.current !== '') {
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        const paragraph = $createParagraphNode();
        root.append(paragraph);
      });
      lastInitialValue.current = initialValue;
      return;
    }

    // Only initialize with content if we haven't initialized yet and there's content
    if (isInitialized.current || !initialValue) {
      lastInitialValue.current = initialValue;
      return;
    }

    const initialize = () => {
      editor.update(() => {
        const root = $getRoot();
        // Only initialize if editor is empty
        if (root.getTextContent() === '') {
          // Clean up content for editing: remove <br> tags from empty paragraphs
          const contentForEditing = initialValue.replace(/<p([^>]*)><br\s*\/?><\/p>/gi, '<p$1></p>');

          const parser = new DOMParser();
          const dom = parser.parseFromString(contentForEditing, 'text/html');
          const nodes = $generateNodesFromDOM(editor, dom);

          root.clear();

          // Filter to only include element and decorator nodes (not text nodes)
          const validNodes = nodes.filter(node => {
            const type = node.getType();
            return type !== 'text' && type !== 'linebreak';
          });

          if (validNodes.length > 0) {
            root.append(...validNodes);
          } else {
            // If no valid nodes, add an empty paragraph
            root.append($createParagraphNode());
          }
        }
      });
      isInitialized.current = true;
    };

    // First try immediately
    initialize();

    // Fallback in case editor isn't ready
    const unregister = editor.registerUpdateListener(() => {
      if (!isInitialized.current) {
        initialize();
      }
    });

    lastInitialValue.current = initialValue;
    return () => unregister();
  }, [editor, initialValue]);

  return null;
}

// Change Handler Plugin
function ChangeHandlerPlugin({ onChange }) {
  const [editor] = useLexicalComposerContext();

  const handleChange = useCallback((editorState) => {
    editorState.read(() => {
      const root = $getRoot();
      const children = root.getChildren();

      // If there's only one empty paragraph, return empty string
      if (
        children.length === 1 &&
        children[0].getType() === 'paragraph' &&
        children[0].getTextContent() === ''
      ) {
        onChange('');
        return;
      }

      // Generate HTML normally
      let htmlString = $generateHtmlFromNodes(editor, null);

      // Remove only the very last empty paragraph if there are multiple paragraphs
      if (children.length > 1) {
        const lastChild = children[children.length - 1];
        if (lastChild.getType() === 'paragraph' && lastChild.getTextContent().trim() === '') {
          htmlString = htmlString.replace(/<p[^>]*>(\s|<br\s*\/?>|&nbsp;)*<\/p>\s*$/i, '');
        }
      }

      onChange(htmlString);
    });
  }, [editor, onChange]);

  return <OnChangePlugin onChange={handleChange} />;
}

// Custom Placeholder Plugin - hides placeholder when embeds are present
function CustomPlaceholderPlugin({ placeholder }) {
  const intl = useIntl();
  const [editor] = useLexicalComposerContext();
  const [showPlaceholder, setShowPlaceholder] = useState(true);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const root = $getRoot();
        const children = root.getChildren();

        // Check if there's any real content (text or embeds)
        const hasContent = children.some(child => {
          const type = child.getType();
          // Check for embed nodes
          if (type === 'youtube-embed' || type === 'vimeo-embed' || type === 'soundcloud-embed') {
            return true;
          }
          // Check for text content in other nodes
          if (child.getTextContent().trim() !== '') {
            return true;
          }
          return false;
        });

        setShowPlaceholder(!hasContent);
      });
    });
  }, [editor]);

  if (!showPlaceholder) {
    return null;
  }

  return <div className={css.editorPlaceholder}>{placeholder || intl.formatMessage({ id: 'FieldRichTextInput.placeholder' })}</div>;
}

// Plugin to ensure paragraphs exist before/after embeds for cursor positioning
function EnsureEdgeParagraphsPlugin() {
  const [editor] = useLexicalComposerContext();
  const isProcessing = useRef(false);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState, tags }) => {
      // Skip if we're already processing or if this is our own update
      if (isProcessing.current || tags.has('ensure-edge-paragraphs')) {
        return;
      }

      editorState.read(() => {
        const root = $getRoot();
        const children = root.getChildren();

        if (children.length === 0) return;

        const firstChild = children[0];
        const lastChild = children[children.length - 1];
        const firstType = firstChild.getType();
        const lastType = lastChild.getType();

        const embedTypes = ['youtube-embed', 'vimeo-embed', 'soundcloud-embed'];
        const isFirstEmbed = embedTypes.includes(firstType);
        const isLastEmbed = embedTypes.includes(lastType);

        if (isFirstEmbed || isLastEmbed) {
          isProcessing.current = true;
          editor.update(() => {
            const root = $getRoot();
            const children = root.getChildren();

            if (children.length === 0) {
              isProcessing.current = false;
              return;
            }

            const firstChild = children[0];
            const lastChild = children[children.length - 1];

            // Add paragraph at start if first node is embed
            if (embedTypes.includes(firstChild.getType())) {
              const paragraph = $createParagraphNode();
              firstChild.insertBefore(paragraph);
            }

            // Add paragraph at end if last node is embed
            if (embedTypes.includes(lastChild.getType())) {
              const paragraph = $createParagraphNode();
              lastChild.insertAfter(paragraph);
            }
          }, { tag: 'ensure-edge-paragraphs' });
          isProcessing.current = false;
        }
      });
    });
  }, [editor]);

  return null;
}

// Auto-embed Plugin - converts pasted embed URLs to embeds
function AutoEmbedPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Listen for paste events
    const removePasteListener = editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        const clipboardData = event instanceof ClipboardEvent ? event.clipboardData : null;
        if (!clipboardData) return false;

        const pastedText = clipboardData.getData('text/plain').trim();
        if (!pastedText) return false;

        // Check if the pasted text is a video URL
        let embedNode = null;

        if (isYouTubeUrl(pastedText)) {
          const videoId = extractYouTubeVideoId(pastedText);
          if (videoId) {
            embedNode = $createYouTubeEmbedNode(videoId);
          }
        } else if (isVimeoUrl(pastedText)) {
          const videoId = extractVimeoVideoId(pastedText);
          if (videoId) {
            embedNode = $createVimeoEmbedNode(videoId);
          }
        } else if (isSoundCloudUrl(pastedText)) {
          embedNode = $createSoundCloudEmbedNode(pastedText);
        }

        if (embedNode) {
          // Prevent default paste behavior
          event.preventDefault();

          // Insert the embed node - EnsureEdgeParagraphsPlugin will add paragraph after if needed
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              $insertNodes([embedNode]);
            }
          });

          return true; // Command handled
        }

        return false; // Let default paste behavior continue
      },
      COMMAND_PRIORITY_HIGH
    );

    return () => {
      removePasteListener();
    };
  }, [editor]);

  return null;
}

// URL Toast Plugin - detects blocked URLs on paste and on typing
function UrlToastPlugin({ onBlockedUrl, blockedUrlMessage }) {
  const [editor] = useLexicalComposerContext();
  const lastToastTime = useRef(0);
  const hadBlockedUrl = useRef(false);

  const fireToast = useCallback(() => {
    const now = Date.now();
    if (now - lastToastTime.current > 3000) {
      lastToastTime.current = now;
      if (onBlockedUrl) {
        onBlockedUrl();
      } else {
        showToaster('error', blockedUrlMessage);
      }
    }
  }, [onBlockedUrl, blockedUrlMessage]);

  // Detect URLs on paste via native DOM event
  useEffect(() => {
    const rootElement = editor.getRootElement();
    if (!rootElement) return;

    const handlePaste = (event) => {
      const pastedText = event.clipboardData?.getData('text/plain')?.trim();
      if (!pastedText) return;

      if (isYouTubeUrl(pastedText) || isVimeoUrl(pastedText) || isSoundCloudUrl(pastedText)) {
        return;
      }

      if (containsBlockedLink(pastedText)) {
        fireToast();
      }
    };

    rootElement.addEventListener('paste', handlePaste);
    return () => rootElement.removeEventListener('paste', handlePaste);
  }, [editor, fireToast]);

  // Detect URLs as they're typed via editor state changes
  useEffect(() => {
    // Initialize ref with current state so we don't toast on page load
    const isFirstUpdate = { current: true };

    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const text = $getRoot().getTextContent();
        const hasBlocked = containsBlockedLink(text);

        if (isFirstUpdate.current) {
          // Skip first update (initial content load) — just record current state
          isFirstUpdate.current = false;
          hadBlockedUrl.current = hasBlocked;
          return;
        }

        // Only fire toast on the transition from no-blocked-URL to has-blocked-URL
        if (hasBlocked && !hadBlockedUrl.current) {
          fireToast();
        }
        hadBlockedUrl.current = hasBlocked;
      });
    });
  }, [editor, fireToast]);

  return null;
}

// Main Editor Component
function Editor({
  placeholder,
  initialValue,
  onChange,
  hideToolbar,
  onBlockedUrl,
  blockedUrlMessage,
  intl,
}) {
  const initialConfig = {
    namespace: 'FieldRichTextInput',
    theme: {
      paragraph: 'editor-paragraph',
      text: {
        bold: 'editor-bold',
        italic: 'editor-italic',
      },
      list: {
        ul: 'editor-list-ul',
        ol: 'editor-list-ol',
        listitem: 'editor-listitem',
      },
    },
    nodes: [HeadingNode, ListNode, ListItemNode, QuoteNode, YouTubeEmbedNode, SoundCloudEmbedNode, VimeoEmbedNode],
    onError: (error) => console.error('Lexical error:', error),
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className={css.editorContainer}>
        {!hideToolbar && (
          <ToolbarPlugin intl={intl} />
        )}
        <div className={css.editorWrapper}>
          <RichTextPlugin
            contentEditable={<ContentEditable className={css.editor} />}
            placeholder={null}
            ErrorBoundary={({ children }) => children}
          />
          <CustomPlaceholderPlugin placeholder={placeholder} />
          <EnsureEdgeParagraphsPlugin />
          <AutoEmbedPlugin />
          <ChangeHandlerPlugin onChange={onChange} />
          <HistoryPlugin />
          <ListPlugin />
          <InitializeContentPlugin initialValue={initialValue} />
        </div>
      </div>
    </LexicalComposer>
  );
}

// Field Component
const FieldRichTextInputComponent = (props) => {
  const {
    rootClassName,
    className,
    wrapperClassName,
    id,
    label,
    input,
    meta,
    hideToolbar,
    placeholder,
    onBlockedUrl,
    blockedUrlMessage,
    intl,
    ...rest
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  // Show error if touched OR if there's content and an error (for max length validation)
  const hasError = meta?.error && (meta?.touched || input?.value);
  const classes = classNames(rootClassName || css.root, className);
  const wrapperClasses = classNames(css.wrapper, wrapperClassName, {
    [css.wrapperSuccess]: meta?.valid && meta?.touched,
    [css.wrapperError]: hasError,
  });

  const handleChange = useCallback(
    (htmlContent) => input?.onChange?.(htmlContent),
    [input]
  );

  return (
    <div className={classes}>
      {label && <label htmlFor={id}>{label}</label>}
      <div className={wrapperClasses}>
        <Editor
          placeholder={placeholder}
          initialValue={input?.value || ''}
          onChange={handleChange}
          hideToolbar={hideToolbar}
          onBlockedUrl={onBlockedUrl}
          blockedUrlMessage={blockedUrlMessage}
          intl={intl}
        />
      </div>
      <ValidationError fieldMeta={{ touched: meta?.touched || !!input?.value, error: meta?.error }} />
    </div>
  );
};

FieldRichTextInputComponent.defaultProps = {
  rootClassName: null,
  className: null,
  wrapperClassName: null,
  id: null,
  label: null,
  hideToolbar: false,
  placeholder: null,
  onBlockedUrl: null,
  meta: {},
  input: { value: '', onChange: () => {} },
};

FieldRichTextInputComponent.propTypes = {
  rootClassName: PropTypes.string,
  className: PropTypes.string,
  wrapperClassName: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  hideToolbar: PropTypes.bool,
  placeholder: PropTypes.string,
  onBlockedUrl: PropTypes.func,
  intl: intlShape.isRequired,
};

const FieldRichTextInputWithIntl = injectIntl(FieldRichTextInputComponent);

const FieldRichTextInput = (props) => (
  <Field {...props} component={FieldRichTextInputWithIntl} />
);

FieldRichTextInput.displayName = 'FieldRichTextInput';

export default FieldRichTextInput;
